import React, { useState, useEffect, useCallback } from 'react';
import PanelLayout from '../Panel/PanelLayout';
import { bpAPI } from '../../services/api';

const BPKycLinks = () => {
  const [links, setLinks]       = useState([]);
  const [meta, setMeta]         = useState({});
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [toast, setToast]       = useState('');
  const [showForm, setShowForm] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const [linksRes, mRes] = await Promise.all([
        bpAPI.kycLinks({ page }),
        bpAPI.merchants({ page: 1 }),
      ]);
      setLinks(linksRes.data.data);
      setMeta(linksRes.data.meta || {});
      setMerchants(mRes.data.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLink = async () => {
    if (!selectedMerchant) { showToast('Please select a merchant first.'); return; }
    setCreating(true);
    try {
      const res = await bpAPI.createKycLink({ merchant_id: parseInt(selectedMerchant) });
      showToast('KYC link created! URL: ' + res.data.data.kyc_url);
      setShowForm(false);
      setSelectedMerchant('');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Error creating KYC link.');
    } finally { setCreating(false); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showToast('URL copied to clipboard!');
  };

  const statusColor = (s) => ({
    pending:   { bg: '#FEF3C7', color: '#D97706' },
    submitted: { bg: '#ddd6fe', color: '#7c3aed' },
    approved:  { bg: '#D1FAE5', color: '#059669' },
    rejected:  { bg: '#FEE2E2', color: '#DC2626' },
  }[s] || { bg: '#F1F5F9', color: '#4a4a6a' });

  return (
    <PanelLayout role="business_partner" title="KYC Links" breadcrumb="Dashboard / KYC Links">

      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: '#F0FDF4', border: '1.5px solid #86EFAC', color: '#15803D',
          padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,.1)', maxWidth: 420,
        }}>{toast}</div>
      )}

      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#14142b' }}>KYC Links</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#4a4a6a' }}>
              Create KYC links for your merchants. Approval is done by Admin.
            </p>
          </div>
          <button
            onClick={() => setShowForm(f => !f)}
            style={{ padding: '9px 18px', background: '#14142b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >+ Create KYC Link</button>
        </div>

        {/* Create form */}
        {showForm && (
          <div style={{ background: '#f9f9fc', border: '1.5px solid #e8e8f1', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#14142b', marginBottom: 12 }}>Select Merchant</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={selectedMerchant}
                onChange={e => setSelectedMerchant(e.target.value)}
                style={{ flex: 1, minWidth: 200, padding: '9px 12px', border: '1.5px solid #e8e8f1', borderRadius: 8, fontSize: 13 }}
              >
                <option value="">-- Select Merchant --</option>
                {merchants.map(m => (
                  <option key={m.id} value={m.id}>{m.username} ({m.email})</option>
                ))}
              </select>
              <button
                onClick={createLink}
                disabled={creating || !selectedMerchant}
                style={{ padding: '9px 20px', background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.7 : 1 }}
              >{creating ? 'Creating…' : 'Generate Link'}</button>
              <button
                onClick={() => setShowForm(false)}
                style={{ padding: '9px 14px', background: '#fff', color: '#4a4a6a', border: '1px solid #e8e8f1', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
              >Cancel</button>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ border: '1px solid #e8e8f1', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 1.5fr 120px',
            background: '#f9f9fc', padding: '10px 16px',
            fontSize: 10, fontWeight: 700, color: '#4a4a6a', letterSpacing: 1, textTransform: 'uppercase',
          }}>
            <span>Token</span><span>Status</span><span>KYC URL</span><span>Created</span>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86' }}>Loading…</div>}

          {!loading && links.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: '#6e6e86', fontSize: 13 }}>
              No KYC links yet. Create one above to get started.
            </div>
          )}

          {!loading && links.map((l, i) => {
            const sc = statusColor(l.status);
            return (
              <div key={l.id} style={{
                display: 'grid', gridTemplateColumns: '1fr 100px 1.5fr 120px',
                padding: '13px 16px', alignItems: 'center',
                borderTop: '1px solid #f0f0f6',
                background: i % 2 === 0 ? '#fff' : '#FAFAFA',
              }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#4a4a6a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {l.token?.slice(0, 16)}…
                </div>
                <span style={{ background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, display: 'inline-block' }}>
                  {l.status}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#4a4a6a', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{l.kyc_url}</span>
                  <button
                    onClick={() => copyUrl(l.kyc_url)}
                    style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >Copy</button>
                </div>
                <div style={{ fontSize: 11, color: '#6e6e86' }}>
                  {l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN') : '—'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <div style={{ marginTop: 16, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 16px', fontSize: 12, color: '#92400E' }}>
          ℹ️ KYC approval is done by the Super Admin only. You can create and share links with your merchants.
        </div>
      </div>
    </PanelLayout>
  );
};

export default BPKycLinks;
